/**
 * V2AdminWorkshopPage — "The Workshop" inside Kindra OS.
 * Route: /admin/os/workshop            (session list + create)
 *        /admin/os/workshop/:sessionId (guided chat, Michael supervises)
 * Phase 1: onboarding chat + context capture + live team preview only.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Chrome, C } from './V2AdminOSPage';
import V2WorkshopChat from './V2WorkshopChat';
import V2WorkshopBuildPanel, { StagePill } from './V2WorkshopBuildPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('kindra-admin-token')}` } });

const S = { hair: '#2A2070', surface: '#1A1150' };

export default function V2AdminWorkshopPage() {
  const [sessions, setSessions] = useState(null);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [bizName, setBizName] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const nav = useNavigate();

  const load = () => axios.get(`${API}/admin/workshop/sessions`, auth())
    .then((r) => setSessions(r.data.sessions))
    .catch((e) => {
      if (e?.response?.status === 401) { localStorage.removeItem('kindra-admin-token'); window.location.assign('/admin/login?returnTo=%2Fadmin%2Fos%2Fworkshop'); return; }
      setErr('Failed to load Workshop sessions.');
    });
  useEffect(() => { load(); }, []);

  const create = async (mode) => {
    if (creating) return;
    setCreating(true); setShareLink('');
    try {
      const r = await axios.post(`${API}/admin/workshop/sessions`,
        { mode, business_name: bizName, notes }, auth());
      if (mode === 'guided') { nav(`/admin/os/workshop/${r.data.session_id}`); return; }
      setShareLink(r.data.share_url);
      try { await navigator.clipboard.writeText(r.data.share_url); } catch {}
      load();
    } catch { setErr('Could not create session.'); }
    setCreating(false);
  };

  return (
    <Chrome activeTab="workshop">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight" style={{ fontFamily: 'Poppins', color: C.icy }}
              data-testid="workshop-title">
            The Workshop
          </h1>
          <p className="text-[14px] mt-1" style={{ color: C.muted, fontFamily: 'Inter' }}>
            Set up AI for a client — a soft chat captures their business, and their AI team takes shape live.
          </p>
        </div>
        <button data-testid="workshop-new-btn" onClick={() => { setShowNew((v) => !v); setShareLink(''); }}
                className="px-5 py-2.5 rounded-full text-[14px] font-bold"
                style={{ background: 'linear-gradient(95deg,#34D1B0,#1FB6C1,#4F8FD0,#9156C4,#EB258D)',
                         color: '#fff', border: 'none', fontFamily: 'Poppins', cursor: 'pointer' }}>
          + New session
        </button>
      </div>

      {showNew && (
        <div className="rounded-2xl p-6 mb-8 max-w-[640px]" data-testid="workshop-new-form"
             style={{ background: S.surface, border: `1px solid ${S.hair}` }}>
          <p className="text-[15px] font-bold mb-4" style={{ color: C.icy, fontFamily: 'Poppins' }}>New Workshop session</p>
          <input data-testid="workshop-bizname-input" value={bizName} onChange={(e) => setBizName(e.target.value)}
                 placeholder="Business name (optional — the chat will find it)"
                 className="w-full px-4 py-2.5 mb-3 text-[14px] outline-none"
                 style={{ background: C.navy, border: `1px solid ${S.hair}`, borderRadius: 12, color: C.icy, fontFamily: 'Inter' }} />
          <textarea data-testid="workshop-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder="Notes for the agent (optional — e.g. 'met at expo, plumber, keen on missed calls')"
                    className="w-full px-4 py-2.5 mb-4 text-[14px] outline-none resize-none"
                    style={{ background: C.navy, border: `1px solid ${S.hair}`, borderRadius: 12, color: C.icy, fontFamily: 'Inter' }} />
          <div className="flex flex-wrap gap-3">
            <button data-testid="workshop-start-guided-btn" onClick={() => create('guided')} disabled={creating}
                    className="px-5 py-2.5 rounded-full text-[13.5px] font-bold"
                    style={{ background: 'rgba(52,209,176,0.14)', color: '#7FE8CE',
                             border: '1px solid rgba(52,209,176,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
              Start guided chat →
            </button>
            <button data-testid="workshop-create-link-btn" onClick={() => create('self_serve')} disabled={creating}
                    className="px-5 py-2.5 rounded-full text-[13.5px] font-bold"
                    style={{ background: 'rgba(79,143,208,0.14)', color: '#B8CDE8',
                             border: '1px solid rgba(79,143,208,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
              Create shareable client link
            </button>
          </div>
          {shareLink && (
            <div className="mt-4 px-4 py-3 rounded-xl text-[13px]" data-testid="workshop-share-link"
                 style={{ background: 'rgba(52,209,176,0.08)', border: '1px solid rgba(52,209,176,0.3)',
                          color: C.icy, fontFamily: 'Inter', wordBreak: 'break-all' }}>
              ✓ Link copied — send it to the client:<br /><strong>{shareLink}</strong>
            </div>
          )}
        </div>
      )}

      {err && <p className="text-[14px] mb-4" style={{ color: '#FF7DB3', fontFamily: 'Inter' }}>{err}</p>}

      {sessions === null ? (
        <p style={{ color: C.faint, fontFamily: 'Inter' }}>Loading…</p>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl p-10 text-center" style={{ background: 'rgba(26,17,80,0.4)', border: `1px dashed ${S.hair}` }}>
          <p className="text-[15px]" style={{ color: C.muted, fontFamily: 'Inter' }}>
            No Workshop sessions yet. Start one above — guided (you drive it with the client) or send them a link.
          </p>
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
             data-testid="workshop-session-list">
          {sessions.map((s) => (
            <Link key={s.session_id} to={`/admin/os/workshop/${s.session_id}`}
                  data-testid={`workshop-session-${s.session_id}`}
                  className="rounded-2xl p-5 block"
                  style={{ background: S.surface, border: `1px solid ${S.hair}`, textDecoration: 'none' }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[15px] font-bold truncate" style={{ color: C.icy, fontFamily: 'Poppins' }}>
                  {s.client_label || 'New client'}
                </p>
                <StagePill stage={s.stage} />
              </div>
              <p className="text-[12px] mb-3" style={{ color: C.faint, fontFamily: 'Inter' }}>
                {s.mode === 'guided' ? 'Guided by you' : 'Self-serve link'} · standard team + {s.team_count} custom
                {s.delivery_mode && <span> · {s.delivery_mode === 'handover' ? 'zip handover' : 'Kindra-managed'}</span>}
                {s.addons_count > 0 && (
                  <span data-testid={`workshop-addons-badge-${s.session_id}`}
                        className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold"
                        style={{ color: '#F0C36D', background: 'rgba(240,195,109,0.10)',
                                 border: '1px solid rgba(240,195,109,0.35)' }}>
                    💰 {s.addons_count} add-on{s.addons_count === 1 ? '' : 's'}
                  </span>
                )}
              </p>
              <div style={{ height: 3, background: 'rgba(220,240,245,0.08)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${s.progress?.pct ?? 0}%`, borderRadius: 99,
                              background: 'linear-gradient(95deg,#34D1B0,#4F8FD0,#EB258D)' }} />
              </div>
              <p className="text-[11.5px] mt-1.5" style={{ color: C.faint, fontFamily: 'Inter' }}>{s.progress?.label}</p>
            </Link>
          ))}
        </div>
      )}
    </Chrome>
  );
}

export const V2AdminWorkshopSessionPage = () => {
  const { sessionId } = useParams();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/workshop/sessions/${sessionId}`, auth())
      .then((r) => setDetail(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) { localStorage.removeItem('kindra-admin-token'); window.location.assign('/admin/login'); return; }
        setErr('Session not found.');
      });
  }, [sessionId]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(detail.share_url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  return (
    <Chrome activeTab="workshop" crumb={<span><Link to="/admin/os/workshop" style={{ color: C.muted }}>The Workshop</Link> · {detail?.client_label || '…'}</span>}>
      {err && <p style={{ color: '#FF7DB3', fontFamily: 'Inter' }}>{err}</p>}
      {detail && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <h1 className="text-[22px] font-extrabold" style={{ fontFamily: 'Poppins', color: C.icy }}>
                {detail.client_label || 'New client'}
              </h1>
              <p className="text-[13px]" style={{ color: C.muted, fontFamily: 'Inter' }}>
                {detail.mode === 'guided' ? 'Guided session — the agent drives, you supervise.' : 'Self-serve session.'}
              </p>
            </div>
            <button data-testid="workshop-copy-link-btn" onClick={copy}
                    className="px-4 py-2 rounded-full text-[13px] font-semibold"
                    style={{ background: 'transparent', color: C.muted, border: `1px solid ${S.hair}`,
                             fontFamily: 'Inter', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : 'Copy client link'}
            </button>
          </div>
          <V2WorkshopChat token={detail.token} showAddons />
          <V2WorkshopBuildPanel sessionId={sessionId} token={detail.token}
            sessionReady={Boolean((detail.phase2 || {}).ready || detail.status === 'complete')} />
        </>
      )}
    </Chrome>
  );
};
